import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import { serviceCities } from '../data/mock';

const BOUNDS = serviceCities.map((c) => [c.lat, c.lng]);
const COVERAGE_RADIUS_M = 8000;

const pinIcon = L.divIcon({
  className: '',
  html: '<div style="width:22px;height:22px;background:#dc2626;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.35)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

// Flies to the selected city and opens its popup.
const FlyTo = ({ city, markers }) => {
  const map = useMap();
  useEffect(() => {
    if (!city) return;
    map.flyTo([city.lat, city.lng], 11, { duration: 0.8 });
    markers.current[city.name]?.openPopup();
  }, [city, map, markers]);
  return null;
};

const CityList = ({ selected, onSelect }) => (
  <ul className="grid grid-cols-2 lg:grid-cols-1 gap-2" data-testid="service-area-list">
    {serviceCities.map((c) => (
      <li key={c.name}>
        <button
          type="button"
          onClick={() => onSelect(c)}
          data-testid={`service-city-${c.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-sm border transition-colors ${
            selected?.name === c.name ? 'border-red-600 bg-red-50 text-neutral-950' : 'border-neutral-200 bg-white text-neutral-700 hover:border-red-600'
          }`}
        >
          <MapPin className="w-4 h-4 text-red-600 shrink-0" />
          <span className="text-sm font-semibold">{c.name}</span>
        </button>
      </li>
    ))}
  </ul>
);

export const ServiceAreaMap = () => {
  const [selected, setSelected] = useState(null);
  const markers = useRef({});

  return (
    <div className="mt-16 max-w-5xl mx-auto" data-testid="service-area-map-section">
      <div className="mb-6">
        <h3 className="font-display italic uppercase text-3xl font-extrabold text-neutral-950">Where We Detail</h3>
        <p className="text-neutral-600 text-sm md:text-base">We come to you anywhere in the shaded area. Tap a city to zoom in.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
        <div className="relative isolate z-0 h-[380px] md:h-[460px] rounded-sm overflow-hidden border border-neutral-200 shadow-xl shadow-neutral-900/5" data-testid="service-area-map">
          <MapContainer bounds={BOUNDS} boundsOptions={{ padding: [40, 40] }} scrollWheelZoom={false} className="w-full h-full service-area-map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={`https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`}
            />
            {serviceCities.map((c) => (
              <Circle
                key={`area-${c.name}`}
                center={[c.lat, c.lng]}
                radius={COVERAGE_RADIUS_M}
                pathOptions={{ color: '#dc2626', weight: 0, fillColor: '#dc2626', fillOpacity: 0.12 }}
              />
            ))}
            {serviceCities.map((c) => (
              <Marker
                key={c.name}
                position={[c.lat, c.lng]}
                icon={pinIcon}
                ref={(m) => { markers.current[c.name] = m; }}
                eventHandlers={{ click: () => setSelected(c) }}
              >
                <Popup>
                  <strong>{c.name}</strong><br />Mobile detailing available
                </Popup>
              </Marker>
            ))}
            <FlyTo city={selected} markers={markers} />
          </MapContainer>
        </div>
        <CityList selected={selected} onSelect={setSelected} />
      </div>
    </div>
  );
};
